// Hint.tsx — "document by design, not text": the global Erklärungen (explanations)
// toggle's plumbing. `showExplanations` (lib/settings.ts) is OFF by default so the UI
// reads clean via labels/structure alone; flipping it on (ⓘ icon in the app bar/drawer/
// sidebar, or the switch in Einstellungen -> Darstellung, all sharing this one piece of
// app.tsx state) reveals the teaching/convenience text inline wherever it's wrapped in
// <Hint>.
//
// A plain Preact context (not a prop threaded through every view/component) — CockpitView
// -> ScenarioEditor -> ForecastFields -> NumberField is exactly the kind of call chain a
// single boolean would otherwise have to thread through untouched at every level; every
// leaf that needs it just calls useExplain() directly, same as any other cross-cutting
// concern (theme, routing).
//
// What belongs in <Hint>: PURELY INSTRUCTIONAL/convenience text — field usage hints
// ("Erhöht die monatliche Rate…", "Standard: 27,5 %…"), section descriptions, chart-symbol
// legends ("Klick auf einen Namen…", "Punktierte Linien: …"), the Rendite-p.a. methodology
// paragraph, Sonderzahlung/Entnahme/Monte-Carlo/inflation feature descriptions.
//
// What must NEVER be wrapped (always renders): the "not tax advice" disclaimer, the
// "…keine Prognose. Anpassbar." prefill warning, ageDataMissing/basisCorrectionApproximate/
// distributionTaxApproximate/rangeClamped banners, disabled-feature reasons (FundPicker's
// "deaktiviert"/"nicht mehr verfügbar" notes, coverage-gated warnings), the
// negative-Verrechnungskonto note, TER/D-007 approximation footnotes, methodology
// disclosures that affect how a shown NUMBER should be read, empty-state explanations,
// action feedback ("Kopiert.", "Aktualisierung abgeschlossen.") and error messages — all of
// that is honesty/state, not teaching, and stays visible regardless of this toggle. When a
// given string was ambiguous, it was kept visible (see the redesign task notes) rather than
// gated.
import { createContext } from 'preact';
import type { ComponentChildren } from 'preact';
import { useContext } from 'preact/hooks';

const ExplainContext = createContext<boolean>(false);

export function ExplainProvider({ value, children }: { value: boolean; children: ComponentChildren }) {
  return <ExplainContext.Provider value={value}>{children}</ExplainContext.Provider>;
}

/** Reads the current Erklärungen toggle state — for a call site that needs to build a
 * conditional `hint` string (NumberField/ToggleField's `hint` prop) rather than wrap a
 * child. */
export function useExplain(): boolean {
  return useContext(ExplainContext);
}

/** Renders `children` only while the Erklärungen toggle is on; otherwise renders nothing.
 * For wrapping a whole element (a `<p class="field-hint">`, a paragraph) rather than
 * building a conditional string — see useExplain() for the latter. */
export function Hint({ children }: { children: ComponentChildren }) {
  const show = useExplain();
  if (!show) return null;
  return <>{children}</>;
}
